import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'
import { parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { ConsequenceMatrixSelection } from './geckoRiskAssessmentConfig'
import { Severity } from './geckoSiteObservation'

export interface DeficiencyMarkupBounds {
  x_start: number
  y_start: number
  x_end: number
  y_end: number
  unit: string
}

function parseMarkupBounds(input: unknown): DeficiencyMarkupBounds | undefined {
  if (!input || typeof input !== 'object') return undefined
  const obj = input as Record<string, unknown>
  const x_start = typeof obj.x_start === 'number' ? obj.x_start : undefined
  const y_start = typeof obj.y_start === 'number' ? obj.y_start : undefined
  const x_end = typeof obj.x_end === 'number' ? obj.x_end : undefined
  const y_end = typeof obj.y_end === 'number' ? obj.y_end : undefined
  const unit = typeof obj.unit === 'string' ? obj.unit : undefined
  if (
    x_start === undefined ||
    y_start === undefined ||
    x_end === undefined ||
    y_end === undefined ||
    unit === undefined
  ) {
    return undefined
  }
  return { x_start, y_start, x_end, y_end, unit }
}

function parseMarkupBoundsList(
  input: unknown
): DeficiencyMarkupBounds[] | undefined {
  // TODO: remove fall back once we migrate all existing deficiencies to the new format
  const raw = Array.isArray(input) ? input : [input]
  const parsed = raw
    .map(parseMarkupBounds)
    .filter((b): b is DeficiencyMarkupBounds => b !== undefined)
  return parsed.length > 0 ? parsed : undefined
}

export interface DeficiencyPhoto {
  id: string
  filename: string
}

function parsePhotos(input: unknown): DeficiencyPhoto[] | undefined {
  if (!Array.isArray(input)) return undefined
  const photos = input.filter(
    (p): p is DeficiencyPhoto =>
      typeof p?.id === 'string' && typeof p?.filename === 'string'
  )
  return photos.length > 0 ? photos : undefined
}

export type GeckoComponentDeficiencyData = {
  title: string
  description?: string
  recommendations?: string
  severity?: Severity
  riskScore?: number
  likelihood?: number
  consequence?: number
  markupBounds?: DeficiencyMarkupBounds[]
  consequenceMatrixSelections?: ConsequenceMatrixSelection[]
  parentDeficiencyId?: string
  photos?: DeficiencyPhoto[]
  preferredPhotoId?: string
}

export class GeckoComponentDeficiency extends GeckoEntryBase<
  GeckoComponentDeficiencyData,
  {
    organization: string
    site: string
    unit: string
    component?: string
    [RolodexEntryType.INSPECTION]?: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        title: parseString(json.data.title) ?? 'Untitled Deficiency',
        description: parseString(json.data.description),
        recommendations: parseString(json.data.recommendations),
        severity: parseString(json.data.severity) as Severity | undefined,
        riskScore: parseNumber(json.data.riskScore),
        likelihood: parseNumber(json.data.likelihood),
        consequence: parseNumber(json.data.consequence),
        markupBounds: parseMarkupBoundsList(json.data.markupBounds),
        consequenceMatrixSelections: json.data.consequenceMatrixSelections as
          | ConsequenceMatrixSelection[]
          | undefined,
        parentDeficiencyId: parseString(json.data.parentDeficiencyId),
        photos: parsePhotos(json.data.photos),
        preferredPhotoId: parseString(json.data.preferredPhotoId),
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component),
        [RolodexEntryType.INSPECTION]: parseString(
          json.tags[RolodexEntryType.INSPECTION]
        ),
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoComponentDeficiency(json)
  }
}
